import type { Member360FullProfile } from './types';

const SRC_SYMITAR = { system: 'symitar' as const, updatedAt: '2026-03-13T08:00:00Z' };
const SRC_CRM     = { system: 'crm'     as const, updatedAt: '2026-03-13T10:00:00Z' };
const SRC_AIRE    = { system: 'aire'    as const, updatedAt: '2026-03-13T09:00:00Z' };

const SRC_SYMITAR_CASCADE = { system: 'symitar' as const, updatedAt: '2026-03-15T08:00:00Z' };
const SRC_CRM_CASCADE     = { system: 'crm'     as const, updatedAt: '2026-03-15T10:00:00Z' };
const SRC_AIRE_CASCADE    = { system: 'aire'    as const, updatedAt: '2026-03-15T09:00:00Z' };

/** Cascade CU spotlight member UUIDs — used by demo routing */
export const CASCADE_SPOTLIGHT_IDS = {
  'sarah-chen': 'cccc0001-0001-4000-8000-cccc00000001',
  'marcus-rivera-lopez': 'cccc0001-0002-4000-8000-cccc00000002',
  'emily-nakamura': 'cccc0001-0003-4000-8000-cccc00000003',
  'robert-kowalski': 'cccc0001-0004-4000-8000-cccc00000004',
  'amara-osei': 'cccc0001-0005-4000-8000-cccc00000005',
} as const;

const CASCADE_TENANT_ID_DEMO = 'a1b2c3d4-0003-4000-8000-333333333333';

const PROFILES: Record<string, Member360FullProfile> = {
  // -------------------------------------------------------------------------
  // Cascade Credit Union spotlight members
  // -------------------------------------------------------------------------

  'cccc0001-0001-4000-8000-cccc00000001': {
    crmMemberId: 'cccc0001-0001-4000-8000-cccc00000001',
    tenantId: CASCADE_TENANT_ID_DEMO,
    fetchedAt: '2026-03-15T10:00:00Z',
    identity: {
      memberNumber: 'CU-CASCADE-SP-001',
      name: 'Sarah Chen',
      maskedSSN: '***-**-1111',
      age: 34,
      memberSince: '2018-06-15',
      fieldOfMembership: 'Community',
      lifecycleStage: 'active',
      lifecycleBadge: 'Active',
      _source: SRC_SYMITAR_CASCADE,
    },
    accountsSummary: {
      totalShareBalance: 67600.50,
      shareCount: 3,
      shares: [
        { shareId: 'cas-sh-001', shareType: 'Checking', balance: 14200.50, dividendRate: 0.0010, openDate: '2018-06-15' },
        { shareId: 'cas-sh-002', shareType: 'Savings',  balance: 28400.00, dividendRate: 0.0050, openDate: '2018-06-15' },
        { shareId: 'cas-sh-003', shareType: 'CD 12-Mo', balance: 25000.00, dividendRate: 0.0510, openDate: '2025-09-01', maturityDate: '2026-09-01' },
      ],
      _source: SRC_SYMITAR_CASCADE,
    },
    loanPipeline: {
      activeLoans: [
        { loanId: 'cas-ln-001', loanType: 'Mortgage', balance: 287500.00, rate: 0.0625, payment: 1920.00, origDate: '2021-03-20', maturityDate: '2051-03-20', dqDays: 0 },
        { loanId: 'cas-ln-002', loanType: 'Auto Loan', balance: 18750.00, rate: 0.0549, payment: 420.00, origDate: '2023-07-12', maturityDate: '2028-07-12', dqDays: 0 },
      ],
      activeLoanCount: 2,
      totalLoanBalance: 306250.00,
      dqDays: 0,
      _source: SRC_SYMITAR_CASCADE,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.22,
        mortgage: 0.78,
        cd: 0.45,
        heloc: 0.82,
        personalLoan: 0.31,
        scoredAt: '2026-03-15T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'HELOC',
      topScore: 0.82,
      propensityTier: 'high',
      _source: SRC_AIRE_CASCADE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'ffff0001-0001-4000-8000-intcap000001',
          tenantId: CASCADE_TENANT_ID_DEMO,
          memberId: 'cccc0001-0001-4000-8000-cccc00000001',
          staffId: 'staff-cas-001',
          type: 'call_recording',
          durationSeconds: 480,
          summary: 'Member called about HELOC rates for planned kitchen renovation. Quoted current rates at 7.49% variable. Discussed $40K-60K HELOC range. Follow-up: send HELOC comparison sheet and schedule in-branch consultation.',
          topics: ['HELOC inquiry', 'home renovation financing', 'rate lock options'],
          actionItems: ['Send HELOC rate comparison sheet by end of week', 'Schedule in-branch consultation within 2 weeks'],
          productMentions: ['HELOC', 'Mortgage'],
          sentimentScore: 0.78,
          followUpDate: '2026-03-28',
          occurredAt: '2026-03-10T14:22:00Z',
          transcriptAvailable: true,
          sourceType: 'call_recording',
          createdAt: '2026-03-10T14:30:00Z',
        },
      ],
      total: 1,
      latestAt: '2026-03-10T14:22:00Z',
      daysSinceLastContact: 5,
      _source: SRC_CRM_CASCADE,
    },
    campaignHistory: {
      campaigns: [
        { campaignId: 'camp0001-0001-4000-8000-cascade0camp1', campaignName: 'Spring Auto Loan Special — 2026 Q1', channel: 'email', sentAt: '2026-01-25', status: 'sent' },
      ],
      total: 1,
      _source: SRC_CRM_CASCADE,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM_CASCADE,
    },
    aireSignals: {
      signals: [
        { signalType: 'heloc_intent', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.82, sourceLabel: 'AIRE' },
        { signalType: 'cd_renewal_likely', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.74, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE_CASCADE,
    },
  },

  'cccc0001-0002-4000-8000-cccc00000002': {
    crmMemberId: 'cccc0001-0002-4000-8000-cccc00000002',
    tenantId: CASCADE_TENANT_ID_DEMO,
    fetchedAt: '2026-03-15T10:00:00Z',
    identity: {
      memberNumber: 'CU-CASCADE-SP-002',
      name: 'Marcus Rivera-Lopez',
      maskedSSN: '***-**-1148',
      age: 42,
      memberSince: '2015-03-22',
      fieldOfMembership: 'Employer Group',
      lifecycleStage: 'at_risk',
      lifecycleBadge: 'At Risk',
      _source: SRC_SYMITAR_CASCADE,
    },
    accountsSummary: {
      totalShareBalance: 1840.22,
      shareCount: 1,
      shares: [
        { shareId: 'cas-sh-010', shareType: 'Checking', balance: 1840.22, dividendRate: 0.0010, openDate: '2015-03-22' },
      ],
      _source: SRC_SYMITAR_CASCADE,
    },
    loanPipeline: {
      activeLoans: [
        { loanId: 'cas-ln-010', loanType: 'Auto Loan', balance: 22100.00, rate: 0.0799, payment: 520.00, origDate: '2023-01-15', maturityDate: '2028-01-15', dqDays: 18 },
      ],
      activeLoanCount: 1,
      totalLoanBalance: 22100.00,
      dqDays: 18,
      _source: SRC_SYMITAR_CASCADE,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.61,
        mortgage: 0.19,
        cd: 0.12,
        heloc: 0.08,
        personalLoan: 0.55,
        scoredAt: '2026-03-15T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'Auto Loan',
      topScore: 0.61,
      propensityTier: 'medium',
      _source: SRC_AIRE_CASCADE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'ffff0001-0002-4000-8000-intcap000001',
          tenantId: CASCADE_TENANT_ID_DEMO,
          memberId: 'cccc0001-0002-4000-8000-cccc00000002',
          staffId: 'staff-cas-005',
          type: 'call_recording',
          durationSeconds: 320,
          summary: 'Inbound call regarding missed auto payment. Member reports temporary income disruption from job transition. Discussed hardship options: skip-a-payment program and loan modification.',
          topics: ['loan delinquency', 'hardship assistance', 'skip-a-payment program'],
          actionItems: ['Process skip-a-payment request for March 2026', 'Schedule 30-day financial wellness check-in for April 17'],
          productMentions: ['Auto Loan', 'Personal Loan'],
          sentimentScore: 0.42,
          followUpDate: '2026-04-17',
          occurredAt: '2026-03-08T10:05:00Z',
          transcriptAvailable: true,
          sourceType: 'call_recording',
          createdAt: '2026-03-08T10:15:00Z',
        },
      ],
      total: 1,
      latestAt: '2026-03-08T10:05:00Z',
      daysSinceLastContact: 7,
      _source: SRC_CRM_CASCADE,
    },
    campaignHistory: {
      campaigns: [],
      total: 0,
      _source: SRC_CRM_CASCADE,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM_CASCADE,
    },
    aireSignals: {
      signals: [
        { signalType: 'churn_risk_elevated', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.86, sourceLabel: 'AIRE' },
        { signalType: 'delinquency_flag', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.91, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE_CASCADE,
    },
  },

  'cccc0001-0003-4000-8000-cccc00000003': {
    crmMemberId: 'cccc0001-0003-4000-8000-cccc00000003',
    tenantId: CASCADE_TENANT_ID_DEMO,
    fetchedAt: '2026-03-15T10:00:00Z',
    identity: {
      memberNumber: 'CU-CASCADE-SP-003',
      name: 'Emily Nakamura',
      maskedSSN: '***-**-1185',
      age: 28,
      memberSince: '2026-01-10',
      fieldOfMembership: 'Association',
      lifecycleStage: 'onboarding',
      lifecycleBadge: 'Onboarding',
      _source: SRC_SYMITAR_CASCADE,
    },
    accountsSummary: {
      totalShareBalance: 58320.00,
      shareCount: 2,
      shares: [
        { shareId: 'cas-sh-020', shareType: 'Checking', balance: 8320.00,  dividendRate: 0.0010, openDate: '2026-01-10' },
        { shareId: 'cas-sh-021', shareType: 'CD 18-Mo', balance: 50000.00, dividendRate: 0.0510, openDate: '2026-01-15', maturityDate: '2027-07-15' },
      ],
      _source: SRC_SYMITAR_CASCADE,
    },
    loanPipeline: {
      activeLoans: [],
      activeLoanCount: 0,
      totalLoanBalance: 0,
      dqDays: 0,
      _source: SRC_SYMITAR_CASCADE,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.38,
        mortgage: 0.42,
        cd: 0.87,
        heloc: 0.14,
        personalLoan: 0.29,
        scoredAt: '2026-03-15T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'CD',
      topScore: 0.87,
      propensityTier: 'high',
      _source: SRC_AIRE_CASCADE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'ffff0001-0003-4000-8000-intcap000001',
          tenantId: CASCADE_TENANT_ID_DEMO,
          memberId: 'cccc0001-0003-4000-8000-cccc00000003',
          staffId: 'staff-cas-005',
          type: 'live_listener',
          durationSeconds: 590,
          summary: 'New member onboarding call at 60-day milestone. Member very satisfied with CD rate decision. Discussed CD ladder strategy. Expressed interest in auto loan when purchasing a second vehicle later this year.',
          topics: ['onboarding review', 'CD ladder strategy', 'auto loan inquiry', 'mobile banking enrollment'],
          actionItems: ['Set 90-day cross-sell outreach reminder for April 10', 'Send CD ladder educational brochure'],
          productMentions: ['CD', 'Auto Loan'],
          sentimentScore: 0.88,
          followUpDate: '2026-04-10',
          occurredAt: '2026-03-12T11:30:00Z',
          transcriptAvailable: true,
          sourceType: 'live_listener',
          createdAt: '2026-03-12T11:40:00Z',
        },
      ],
      total: 1,
      latestAt: '2026-03-12T11:30:00Z',
      daysSinceLastContact: 3,
      _source: SRC_CRM_CASCADE,
    },
    campaignHistory: {
      campaigns: [],
      total: 0,
      _source: SRC_CRM_CASCADE,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM_CASCADE,
    },
    aireSignals: {
      signals: [
        { signalType: 'cd_ladder_opportunity', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.87, sourceLabel: 'AIRE' },
        { signalType: 'mortgage_intent_early', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.69, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE_CASCADE,
    },
  },

  'cccc0001-0004-4000-8000-cccc00000004': {
    crmMemberId: 'cccc0001-0004-4000-8000-cccc00000004',
    tenantId: CASCADE_TENANT_ID_DEMO,
    fetchedAt: '2026-03-15T10:00:00Z',
    identity: {
      memberNumber: 'CU-CASCADE-SP-004',
      name: 'Robert Kowalski',
      maskedSSN: '***-**-1222',
      age: 57,
      memberSince: '2003-09-01',
      fieldOfMembership: 'Community',
      lifecycleStage: 'active',
      lifecycleBadge: 'Active',
      _source: SRC_SYMITAR_CASCADE,
    },
    accountsSummary: {
      totalShareBalance: 62400.75,
      shareCount: 3,
      shares: [
        { shareId: 'cas-sh-030', shareType: 'Checking',    balance: 5100.75,  dividendRate: 0.0010, openDate: '2003-09-01' },
        { shareId: 'cas-sh-031', shareType: 'Savings',     balance: 12300.00, dividendRate: 0.0050, openDate: '2003-09-01' },
        { shareId: 'cas-sh-032', shareType: 'Money Market', balance: 45000.00, dividendRate: 0.0200, openDate: '2010-04-01' },
      ],
      _source: SRC_SYMITAR_CASCADE,
    },
    loanPipeline: {
      activeLoans: [],
      activeLoanCount: 0,
      totalLoanBalance: 0,
      dqDays: 0,
      _source: SRC_SYMITAR_CASCADE,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.17,
        mortgage: 0.09,
        cd: 0.74,
        heloc: 0.51,
        personalLoan: 0.22,
        scoredAt: '2026-03-15T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'CD',
      topScore: 0.74,
      propensityTier: 'high',
      _source: SRC_AIRE_CASCADE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'ffff0001-0004-4000-8000-intcap000001',
          tenantId: CASCADE_TENANT_ID_DEMO,
          memberId: 'cccc0001-0004-4000-8000-cccc00000004',
          staffId: 'staff-cas-004',
          type: 'dictation',
          durationSeconds: 240,
          summary: 'Robert visited the Hillsboro branch for annual relationship review. Discussed rebalancing $45K money market into a 3-year CD ladder. BSA periodic review completed: no concerns. Member mentioned son starting college — discussed education savings options.',
          topics: ['annual relationship review', 'CD ladder rebalancing', 'BSA periodic review', 'education savings'],
          actionItems: ['Prepare 3-year CD ladder rate comparison for next visit', 'Schedule wealth planning consultation with investment team'],
          productMentions: ['CD', 'Savings'],
          sentimentScore: 0.81,
          followUpDate: '2026-04-01',
          occurredAt: '2026-02-28T15:00:00Z',
          transcriptAvailable: false,
          sourceType: 'dictation',
          createdAt: '2026-02-28T15:10:00Z',
        },
      ],
      total: 1,
      latestAt: '2026-02-28T15:00:00Z',
      daysSinceLastContact: 15,
      _source: SRC_CRM_CASCADE,
    },
    campaignHistory: {
      campaigns: [],
      total: 0,
      _source: SRC_CRM_CASCADE,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM_CASCADE,
    },
    aireSignals: {
      signals: [
        { signalType: 'cd_ladder_opportunity', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.74, sourceLabel: 'AIRE' },
        { signalType: 'idle_cash_opportunity', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.68, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE_CASCADE,
    },
  },

  'cccc0001-0005-4000-8000-cccc00000005': {
    crmMemberId: 'cccc0001-0005-4000-8000-cccc00000005',
    tenantId: CASCADE_TENANT_ID_DEMO,
    fetchedAt: '2026-03-15T10:00:00Z',
    identity: {
      memberNumber: 'CU-CASCADE-SP-005',
      name: 'Amara Osei',
      maskedSSN: '***-**-1259',
      age: 39,
      memberSince: '2019-11-08',
      fieldOfMembership: 'Employer Group',
      lifecycleStage: 'active',
      lifecycleBadge: 'Active',
      _source: SRC_SYMITAR_CASCADE,
    },
    accountsSummary: {
      totalShareBalance: 31150.00,
      shareCount: 2,
      shares: [
        { shareId: 'cas-sh-040', shareType: 'Checking', balance: 22400.00, dividendRate: 0.0010, openDate: '2019-11-08' },
        { shareId: 'cas-sh-041', shareType: 'Savings',  balance: 8750.00,  dividendRate: 0.0050, openDate: '2019-11-08' },
      ],
      _source: SRC_SYMITAR_CASCADE,
    },
    loanPipeline: {
      activeLoans: [
        { loanId: 'cas-ln-040', loanType: 'HELOC',    balance: 95000.00, rate: 0.0749, payment: 780.00, origDate: '2022-06-01', dqDays: 0 },
        { loanId: 'cas-ln-041', loanType: 'Personal', balance: 15800.00, rate: 0.0999, payment: 420.00, origDate: '2024-02-20', maturityDate: '2029-02-20', dqDays: 0 },
      ],
      activeLoanCount: 2,
      totalLoanBalance: 110800.00,
      dqDays: 0,
      _source: SRC_SYMITAR_CASCADE,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.34,
        mortgage: 0.48,
        cd: 0.29,
        heloc: 0.76,
        personalLoan: 0.71,
        scoredAt: '2026-03-15T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'HELOC',
      topScore: 0.76,
      propensityTier: 'high',
      _source: SRC_AIRE_CASCADE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'ffff0001-0005-4000-8000-intcap000001',
          tenantId: CASCADE_TENANT_ID_DEMO,
          memberId: 'cccc0001-0005-4000-8000-cccc00000005',
          staffId: 'staff-cas-001',
          type: 'transcript_paste',
          durationSeconds: 410,
          summary: 'Member inquired about opening a business checking account for her interior design practice. Discussed HELOC draw of $25K for leasing commercial studio space. Member qualified; draw processed same session.',
          topics: ['business banking inquiry', 'HELOC draw', 'commercial space financing', 'SBA loan overview'],
          actionItems: ['Process $25K HELOC draw for commercial studio lease', 'Send business checking account opening materials'],
          productMentions: ['HELOC', 'Checking'],
          sentimentScore: 0.85,
          followUpDate: '2026-04-15',
          occurredAt: '2026-03-05T13:45:00Z',
          transcriptAvailable: true,
          sourceType: 'transcript_paste',
          createdAt: '2026-03-05T14:00:00Z',
        },
      ],
      total: 1,
      latestAt: '2026-03-05T13:45:00Z',
      daysSinceLastContact: 10,
      _source: SRC_CRM_CASCADE,
    },
    campaignHistory: {
      campaigns: [],
      total: 0,
      _source: SRC_CRM_CASCADE,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM_CASCADE,
    },
    aireSignals: {
      signals: [
        { signalType: 'heloc_draw_intent', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.76, sourceLabel: 'AIRE' },
        { signalType: 'business_banking_intent', value: true, detectedAt: '2026-03-15T09:00:00Z', confidence: 0.82, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE_CASCADE,
    },
  },

  // -------------------------------------------------------------------------
  // Pilot tenant mock profiles (pre-existing)
  // -------------------------------------------------------------------------
  'mem-001': {
    crmMemberId: 'mem-001',
    tenantId: 'tenant-pilot',
    fetchedAt: '2026-03-14T07:00:00Z',
    identity: {
      memberNumber: '100001',
      name: 'Sarah J. Mitchell',
      maskedSSN: '***-**-4821',
      age: 38,
      memberSince: '2018-03-15',
      fieldOfMembership: 'Community',
      lifecycleStage: 'active',
      lifecycleBadge: 'Active',
      _source: SRC_SYMITAR,
    },
    accountsSummary: {
      totalShareBalance: 34320.50,
      shareCount: 3,
      shares: [
        { shareId: 'sh-001', shareType: 'Checking', balance: 4200.50, dividendRate: 0.0010, openDate: '2018-03-15' },
        { shareId: 'sh-002', shareType: 'Savings',  balance: 18120.00, dividendRate: 0.0050, openDate: '2018-03-15' },
        { shareId: 'sh-003', shareType: 'CD 12-Mo', balance: 12000.00, dividendRate: 0.0475, openDate: '2025-09-01', maturityDate: '2026-09-01' },
      ],
      _source: SRC_SYMITAR,
    },
    loanPipeline: {
      activeLoans: [
        { loanId: 'ln-001', loanType: 'Mortgage', balance: 248500.00, rate: 0.0625, payment: 1840.00, origDate: '2021-06-01', maturityDate: '2051-06-01', dqDays: 0 },
        { loanId: 'ln-002', loanType: 'Auto Loan', balance: 14200.00, rate: 0.0599, payment: 385.00, origDate: '2023-11-10', maturityDate: '2028-11-10', dqDays: 0 },
      ],
      activeLoanCount: 2,
      totalLoanBalance: 262700.00,
      dqDays: 0,
      _source: SRC_SYMITAR,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.28,
        mortgage: 0.82,
        cd: 0.71,
        heloc: 0.65,
        personalLoan: 0.19,
        scoredAt: '2026-03-13T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'Mortgage',
      topScore: 0.82,
      propensityTier: 'high',
      _source: SRC_AIRE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'int-001',
          tenantId: 'tenant-pilot',
          memberId: 'mem-001',
          staffId: 'staff-001',
          type: 'call_recording',
          durationSeconds: 420,
          summary: 'Member inquired about HELOC rates for home renovation. Interested in $40K–$60K range. Follow up with rate sheet.',
          topics: ['heloc', 'home_renovation', 'rates'],
          actionItems: ['Send HELOC rate sheet', 'Schedule follow-up call'],
          productMentions: ['HELOC'],
          sentimentScore: 0.6,
          followUpDate: '2026-03-21',
          occurredAt: '2026-03-13T14:30:00Z',
          transcriptAvailable: true,
          sourceType: 'call_recording',
          createdAt: '2026-03-13T14:35:00Z',
        },
        {
          interactionId: 'int-002',
          tenantId: 'tenant-pilot',
          memberId: 'mem-001',
          staffId: 'staff-002',
          type: 'dictation',
          durationSeconds: null,
          summary: 'Member visited branch to update contact information. No product interest noted.',
          topics: ['contact_update'],
          actionItems: [],
          productMentions: [],
          sentimentScore: 0.1,
          followUpDate: null,
          occurredAt: '2026-02-28T10:15:00Z',
          transcriptAvailable: false,
          sourceType: 'dictation',
          createdAt: '2026-02-28T10:20:00Z',
        },
      ],
      total: 2,
      latestAt: '2026-03-13T14:30:00Z',
      daysSinceLastContact: 1,
      _source: SRC_CRM,
    },
    campaignHistory: {
      campaigns: [
        { campaignId: 'camp-001', campaignName: 'Spring HELOC Promo',    channel: 'email', sentAt: '2026-03-01', status: 'sent' },
        { campaignId: 'camp-002', campaignName: 'CD Maturity Reminder',  channel: 'email', sentAt: '2026-02-15', status: 'sent' },
        { campaignId: 'camp-003', campaignName: 'Mortgage Refi Offer',   channel: 'sms',   sentAt: '2025-11-10', status: 'sent' },
      ],
      total: 3,
      _source: SRC_CRM,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM,
    },
    aireSignals: {
      signals: [
        { signalType: 'heloc_intent',        value: true,  detectedAt: '2026-03-13T09:00:00Z', confidence: 0.87, sourceLabel: 'AIRE' },
        { signalType: 'cd_renewal_likely',   value: true,  detectedAt: '2026-03-10T09:00:00Z', confidence: 0.74, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE,
    },
  },

  'mem-002': {
    crmMemberId: 'mem-002',
    tenantId: 'tenant-pilot',
    fetchedAt: '2026-03-14T07:00:00Z',
    identity: {
      memberNumber: '100002',
      name: 'Marcus T. Rivera',
      maskedSSN: '***-**-7734',
      age: 44,
      memberSince: '2020-07-08',
      fieldOfMembership: 'Community',
      lifecycleStage: 'at_risk',
      lifecycleBadge: 'At Risk',
      _source: SRC_SYMITAR,
    },
    accountsSummary: {
      totalShareBalance: 2150.00,
      shareCount: 1,
      shares: [
        { shareId: 'sh-010', shareType: 'Checking', balance: 2150.00, dividendRate: 0.0010, openDate: '2020-07-08' },
      ],
      _source: SRC_SYMITAR,
    },
    loanPipeline: {
      activeLoans: [
        { loanId: 'ln-010', loanType: 'Auto Loan', balance: 20000.00, rate: 0.0799, payment: 495.00, origDate: '2022-08-15', maturityDate: '2027-08-15', dqDays: 15 },
      ],
      activeLoanCount: 1,
      totalLoanBalance: 20000.00,
      dqDays: 15,
      _source: SRC_SYMITAR,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.44,
        mortgage: 0.12,
        cd: 0.08,
        heloc: 0.05,
        personalLoan: 0.55,
        scoredAt: '2026-03-13T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'Personal Loan',
      topScore: 0.55,
      propensityTier: 'medium',
      _source: SRC_AIRE,
    },
    interactionHistory: {
      interactions: [],
      total: 0,
      latestAt: null,
      daysSinceLastContact: 51,
      _source: SRC_CRM,
    },
    campaignHistory: {
      campaigns: [
        { campaignId: 'camp-010', campaignName: 'DQ Outreach — Auto',  channel: 'sms',   sentAt: '2026-03-01', status: 'sent' },
        { campaignId: 'camp-011', campaignName: 'Personal Loan Offer', channel: 'email', sentAt: '2026-01-15', status: 'sent' },
      ],
      total: 2,
      _source: SRC_CRM,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM,
    },
    aireSignals: {
      signals: [
        { signalType: 'churn_risk_elevated', value: true, detectedAt: '2026-03-13T09:00:00Z', confidence: 0.81, sourceLabel: 'AIRE' },
      ],
      signalCount: 1,
      _source: SRC_AIRE,
    },
  },

  'mem-003': {
    crmMemberId: 'mem-003',
    tenantId: 'tenant-pilot',
    fetchedAt: '2026-03-14T07:00:00Z',
    identity: {
      memberNumber: '100003',
      name: 'Priya K. Nair',
      maskedSSN: '***-**-2291',
      age: 31,
      memberSince: '2026-01-04',
      fieldOfMembership: 'Community',
      lifecycleStage: 'new',
      lifecycleBadge: 'New',
      _source: SRC_SYMITAR,
    },
    accountsSummary: {
      totalShareBalance: 51800.00,
      shareCount: 2,
      shares: [
        { shareId: 'sh-020', shareType: 'Checking', balance: 6800.00,  dividendRate: 0.0010, openDate: '2026-01-04' },
        { shareId: 'sh-021', shareType: 'CD 18-Mo', balance: 45000.00, dividendRate: 0.0510, openDate: '2026-01-04', maturityDate: '2027-07-04' },
      ],
      _source: SRC_SYMITAR,
    },
    loanPipeline: {
      activeLoans: [],
      activeLoanCount: 0,
      totalLoanBalance: 0,
      dqDays: 0,
      _source: SRC_SYMITAR,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.38,
        mortgage: 0.72,
        cd: 0.85,
        heloc: 0.22,
        personalLoan: 0.29,
        scoredAt: '2026-03-13T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'CD',
      topScore: 0.85,
      propensityTier: 'high',
      _source: SRC_AIRE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'int-020',
          tenantId: 'tenant-pilot',
          memberId: 'mem-003',
          staffId: 'staff-001',
          type: 'dictation',
          durationSeconds: null,
          summary: 'New member onboarding call. Transferred $45K from prior bank. Interested in mortgage pre-approval in 6–12 months.',
          topics: ['onboarding', 'mortgage', 'cd'],
          actionItems: ['Send mortgage pre-approval checklist', 'Schedule 90-day check-in'],
          productMentions: ['CD', 'Mortgage'],
          sentimentScore: 0.8,
          followUpDate: '2026-04-04',
          occurredAt: '2026-01-04T11:00:00Z',
          transcriptAvailable: false,
          sourceType: 'dictation',
          createdAt: '2026-01-04T11:10:00Z',
        },
      ],
      total: 1,
      latestAt: '2026-01-04T11:00:00Z',
      daysSinceLastContact: 69,
      _source: SRC_CRM,
    },
    campaignHistory: {
      campaigns: [
        { campaignId: 'camp-020', campaignName: 'New Member Welcome Series', channel: 'email', sentAt: '2026-01-07', status: 'sent' },
      ],
      total: 1,
      _source: SRC_CRM,
    },
    complianceFlags: {
      openBsaAlerts: 0,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: false,
      _source: SRC_CRM,
    },
    aireSignals: {
      signals: [
        { signalType: 'mortgage_intent_early', value: true, detectedAt: '2026-03-13T09:00:00Z', confidence: 0.69, sourceLabel: 'AIRE' },
        { signalType: 'cd_ladder_opportunity', value: true, detectedAt: '2026-03-13T09:00:00Z', confidence: 0.77, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE,
    },
  },

  'mem-004': {
    crmMemberId: 'mem-004',
    tenantId: 'tenant-pilot',
    fetchedAt: '2026-03-14T07:00:00Z',
    identity: {
      memberNumber: '100004',
      name: 'Derek O. Fontaine',
      maskedSSN: '***-**-9103',
      age: 52,
      memberSince: '2015-11-20',
      fieldOfMembership: 'Community',
      lifecycleStage: 'active',
      lifecycleBadge: 'Active',
      _source: SRC_SYMITAR,
    },
    accountsSummary: {
      totalShareBalance: 8750.25,
      shareCount: 2,
      shares: [
        { shareId: 'sh-030', shareType: 'Checking', balance: 3750.25, dividendRate: 0.0010, openDate: '2015-11-20' },
        { shareId: 'sh-031', shareType: 'Savings',  balance: 5000.00, dividendRate: 0.0050, openDate: '2015-11-20' },
      ],
      _source: SRC_SYMITAR,
    },
    loanPipeline: {
      activeLoans: [],
      activeLoanCount: 0,
      totalLoanBalance: 0,
      dqDays: 0,
      _source: SRC_SYMITAR,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.15,
        mortgage: 0.08,
        cd: 0.32,
        heloc: 0.11,
        personalLoan: 0.29,
        scoredAt: '2026-03-13T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'CD',
      topScore: 0.32,
      propensityTier: 'low',
      _source: SRC_AIRE,
    },
    interactionHistory: { interactions: [], total: 0, latestAt: null, daysSinceLastContact: 14, _source: SRC_CRM },
    campaignHistory: { campaigns: [], total: 0, _source: SRC_CRM },
    complianceFlags: {
      openBsaAlerts: 1,
      adverseActionHistory: [],
      fairLendingFlags: 0,
      hasOpenFlags: true,
      _source: SRC_CRM,
    },
    aireSignals: { signals: [], signalCount: 0, _source: SRC_AIRE },
  },

  'mem-005': {
    crmMemberId: 'mem-005',
    tenantId: 'tenant-pilot',
    fetchedAt: '2026-03-14T07:00:00Z',
    identity: {
      memberNumber: '100005',
      name: 'Linda R. Okafor',
      maskedSSN: '***-**-6672',
      age: 61,
      memberSince: '2012-06-30',
      fieldOfMembership: 'Community',
      lifecycleStage: 'dormant',
      lifecycleBadge: 'Dormant',
      _source: SRC_SYMITAR,
    },
    accountsSummary: {
      totalShareBalance: 312.00,
      shareCount: 1,
      shares: [{ shareId: 'sh-040', shareType: 'Savings', balance: 312.00, dividendRate: 0.0050, openDate: '2012-06-30' }],
      _source: SRC_SYMITAR,
    },
    loanPipeline: { activeLoans: [], activeLoanCount: 0, totalLoanBalance: 0, dqDays: 0, _source: SRC_SYMITAR },
    propensityScores: { scores: null, topProduct: null, topScore: null, propensityTier: 'unknown', _source: SRC_AIRE },
    interactionHistory: { interactions: [], total: 0, latestAt: null, daysSinceLastContact: 190, _source: SRC_CRM },
    campaignHistory: { campaigns: [], total: 0, _source: SRC_CRM },
    complianceFlags: { openBsaAlerts: 0, adverseActionHistory: [], fairLendingFlags: 0, hasOpenFlags: false, _source: SRC_CRM },
    aireSignals: { signals: [], signalCount: 0, _source: SRC_AIRE },
  },

  'mem-006': {
    crmMemberId: 'mem-006',
    tenantId: 'tenant-pilot',
    fetchedAt: '2026-03-14T07:00:00Z',
    identity: {
      memberNumber: '100006',
      name: 'James W. Tran',
      maskedSSN: '***-**-3358',
      age: 47,
      memberSince: '2019-09-14',
      fieldOfMembership: 'Community',
      lifecycleStage: 'active',
      lifecycleBadge: 'Active',
      _source: SRC_SYMITAR,
    },
    accountsSummary: {
      totalShareBalance: 39200.00,
      shareCount: 2,
      shares: [
        { shareId: 'sh-050', shareType: 'Checking', balance: 14200.00, dividendRate: 0.0010, openDate: '2019-09-14' },
        { shareId: 'sh-051', shareType: 'Money Mkt', balance: 25000.00, dividendRate: 0.0420, openDate: '2022-01-01' },
      ],
      _source: SRC_SYMITAR,
    },
    loanPipeline: {
      activeLoans: [
        { loanId: 'ln-050', loanType: 'HELOC', balance: 85000.00, rate: 0.0749, payment: 720.00, origDate: '2023-05-01', dqDays: 0 },
        { loanId: 'ln-051', loanType: 'Mortgage', balance: 312000.00, rate: 0.0575, payment: 2180.00, origDate: '2019-09-14', maturityDate: '2049-09-14', dqDays: 0 },
      ],
      activeLoanCount: 2,
      totalLoanBalance: 397000.00,
      dqDays: 0,
      _source: SRC_SYMITAR,
    },
    propensityScores: {
      scores: {
        autoLoan: 0.31,
        mortgage: 0.55,
        cd: 0.62,
        heloc: 0.78,
        personalLoan: 0.14,
        scoredAt: '2026-03-13T09:00:00Z',
        promptId: 'CRM-001',
        promptVersion: 3,
      },
      topProduct: 'HELOC',
      topScore: 0.78,
      propensityTier: 'high',
      _source: SRC_AIRE,
    },
    interactionHistory: {
      interactions: [
        {
          interactionId: 'int-050',
          tenantId: 'tenant-pilot',
          memberId: 'mem-006',
          staffId: 'staff-003',
          type: 'live_listener',
          durationSeconds: 680,
          summary: 'Discussed HELOC draw options and potential CD ladder strategy. Member has $25K idle in money market.',
          topics: ['heloc', 'cd', 'investment'],
          actionItems: ['Send CD ladder proposal', 'Review HELOC draw schedule'],
          productMentions: ['HELOC', 'CD', 'Money Market'],
          sentimentScore: 0.7,
          followUpDate: '2026-03-20',
          occurredAt: '2026-03-12T15:00:00Z',
          transcriptAvailable: true,
          sourceType: 'live_listener',
          createdAt: '2026-03-12T15:15:00Z',
        },
      ],
      total: 1,
      latestAt: '2026-03-12T15:00:00Z',
      daysSinceLastContact: 2,
      _source: SRC_CRM,
    },
    campaignHistory: {
      campaigns: [
        { campaignId: 'camp-050', campaignName: 'HELOC Rate Lock Offer', channel: 'email', sentAt: '2026-03-05', status: 'sent' },
        { campaignId: 'camp-051', campaignName: 'CD Ladder Guide',       channel: 'email', sentAt: '2026-02-01', status: 'sent' },
      ],
      total: 2,
      _source: SRC_CRM,
    },
    complianceFlags: { openBsaAlerts: 0, adverseActionHistory: [], fairLendingFlags: 0, hasOpenFlags: false, _source: SRC_CRM },
    aireSignals: {
      signals: [
        { signalType: 'heloc_draw_intent',      value: true, detectedAt: '2026-03-12T09:00:00Z', confidence: 0.83, sourceLabel: 'AIRE' },
        { signalType: 'idle_cash_opportunity',  value: true, detectedAt: '2026-03-13T09:00:00Z', confidence: 0.71, sourceLabel: 'AIRE' },
      ],
      signalCount: 2,
      _source: SRC_AIRE,
    },
  },
};

const FALLBACK_PROFILE = PROFILES['mem-001'] as Member360FullProfile;

/** Set of known Cascade spotlight UUID values for fast O(1) lookup */
const CASCADE_SPOTLIGHT_UUID_SET: ReadonlySet<string> = new Set<string>(
  Object.values(CASCADE_SPOTLIGHT_IDS) as string[],
);

/**
 * Returns mock profile for a memberId.
 * Checks Cascade spotlight UUIDs, then pilot members, then returns a generic fallback.
 */
export function getMockProfile(memberId: string): Member360FullProfile {
  // Direct profile lookup (covers both Cascade UUIDs and pilot IDs)
  const direct = PROFILES[memberId];
  if (direct) return direct;

  // Check if it's a known Cascade spotlight UUID with no direct match (shouldn't happen)
  if (CASCADE_SPOTLIGHT_UUID_SET.has(memberId)) {
    const cascadeFallback = PROFILES['cccc0001-0001-4000-8000-cccc00000001'] as Member360FullProfile;
    return {
      ...cascadeFallback,
      crmMemberId: memberId,
      identity: { ...cascadeFallback.identity, memberNumber: memberId, name: `Cascade Member ${memberId}` },
    };
  }

  return {
    ...FALLBACK_PROFILE,
    crmMemberId: memberId,
    tenantId: FALLBACK_PROFILE.tenantId,
    identity: { ...FALLBACK_PROFILE.identity, memberNumber: memberId, name: `Member ${memberId}` },
  };
}
